/**
 * A world, written down.
 *
 * The server grew a world in memory and lost it on every restart - which
 * meant every deployment quietly demolished whatever anybody had built. This
 * is the file that stops that.
 *
 * **Only what generation would not produce.** The terrain comes from a seed
 * and the seed is three numbers; storing the whole of it would be storing an
 * answer that can be recomputed. What has to be kept is the difference: the
 * chunks somebody has dug in or built on. A fresh world is a header and
 * nothing else.
 *
 * **Reading is allowed to fail, and says so.** A file that cannot be read is
 * not an empty world, and the difference matters more than anything else
 * here: treated as empty, the next save writes over the thing that could not
 * be read and a bad hour becomes a lost month. So the result is a verdict,
 * and the caller is expected to stop rather than carry on.
 *
 * Pure: bytes in, bytes out. Nothing here opens a file.
 */

import { CHUNK_VOLUME } from "../world/chunk.js";

/** "VYZH". Four bytes that say this is one of ours before anything else. */
export const WORLD_MAGIC = 0x56595a48;

/** Bumped when the layout below changes. An older file is refused, not guessed. */
export const WORLD_FILE_VERSION = 1;

/** Bytes before the first chunk: magic, version, flags, seed, size, count. */
export const WORLD_HEADER = 20;
/** Bytes before each chunk's blocks: three signed coordinates. */
export const CHUNK_RECORD_HEADER = 6;

export interface SavedChunk {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
  readonly blocks: Uint16Array;
}

export interface WorldSnapshot {
  readonly seed: number;
  readonly sizeXZ: number;
  readonly sizeY: number;
  readonly kremlin: boolean;
  readonly chunks: readonly SavedChunk[];
}

/**
 * What came back, or why nothing did.
 *
 * A verdict rather than `null`, because "there is no file yet" and "there is
 * a file and I cannot read it" want opposite things done about them: the
 * first is a new world, the second is hands off.
 */
export type WorldRead =
  | { readonly ok: true; readonly world: WorldSnapshot }
  | { readonly ok: false; readonly why: "empty" | "magic" | "version" | "truncated" };

export function encodeWorld(snapshot: WorldSnapshot): ArrayBuffer {
  const each = CHUNK_RECORD_HEADER + CHUNK_VOLUME * 2;
  const buffer = new ArrayBuffer(WORLD_HEADER + snapshot.chunks.length * each);
  const view = new DataView(buffer);

  view.setUint32(0, WORLD_MAGIC);
  view.setUint16(4, WORLD_FILE_VERSION);
  view.setUint16(6, snapshot.kremlin ? 1 : 0);
  // Signed: a seed is whatever number somebody typed, and half of them are
  // over two billion when read as unsigned.
  view.setInt32(8, snapshot.seed | 0);
  view.setUint16(12, snapshot.sizeXZ);
  view.setUint16(14, snapshot.sizeY);
  view.setUint32(16, snapshot.chunks.length);

  let at = WORLD_HEADER;
  for (const chunk of snapshot.chunks) {
    view.setInt16(at, chunk.cx);
    view.setInt16(at + 2, chunk.cy);
    view.setInt16(at + 4, chunk.cz);
    // Every offset here is even - the header is twenty bytes and a record is
    // six plus a whole number of shorts - so this view is always aligned. Get
    // that wrong and the constructor throws rather than reading rubbish.
    new Uint16Array(buffer, at + CHUNK_RECORD_HEADER, CHUNK_VOLUME)
      .set(chunk.blocks);
    at += each;
  }
  return buffer;
}

export function decodeWorld(buffer: ArrayBuffer): WorldRead {
  if (buffer.byteLength === 0) return { ok: false, why: "empty" };
  if (buffer.byteLength < WORLD_HEADER) return { ok: false, why: "truncated" };

  const view = new DataView(buffer);
  if (view.getUint32(0) !== WORLD_MAGIC) return { ok: false, why: "magic" };
  if (view.getUint16(4) !== WORLD_FILE_VERSION) return { ok: false, why: "version" };

  const count = view.getUint32(16);
  const each = CHUNK_RECORD_HEADER + CHUNK_VOLUME * 2;
  /*
   * The length has to match what the count claims.
   *
   * A file cut off half way through - the disk filled, the process was
   * killed mid-write - would otherwise load as a world missing its last few
   * chunks, which is a world with holes in it and no warning.
   */
  if (buffer.byteLength !== WORLD_HEADER + count * each) {
    return { ok: false, why: "truncated" };
  }

  const chunks: SavedChunk[] = [];
  let at = WORLD_HEADER;
  for (let i = 0; i < count; i++) {
    chunks.push({
      cx: view.getInt16(at),
      cy: view.getInt16(at + 2),
      cz: view.getInt16(at + 4),
      // Copied, not viewed: the caller keeps these and the buffer they came
      // from is about to go out of scope.
      blocks: new Uint16Array(
        buffer.slice(at + CHUNK_RECORD_HEADER, at + each)),
    });
    at += each;
  }

  return {
    ok: true,
    world: {
      seed: view.getInt32(8),
      sizeXZ: view.getUint16(12),
      sizeY: view.getUint16(14),
      kremlin: view.getUint16(6) === 1,
      chunks,
    },
  };
}

/**
 * Is this the world the server is running?
 *
 * A save from a different seed or a different size is not this world, and
 * laying its chunks over this one would put buildings in the sea. Refused
 * rather than adapted.
 */
export function sameWorld(
  snapshot: WorldSnapshot,
  opts: { seed: number; sizeXZ: number; sizeY: number },
): boolean {
  return snapshot.seed === (opts.seed | 0) &&
    snapshot.sizeXZ === opts.sizeXZ &&
    snapshot.sizeY === opts.sizeY;
}
